import {
  DestroyRef,
  Injectable,
  inject,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  select,
  Store,
} from '@ngrx/store';
import find from 'lodash/find';
import {lastValueFrom} from 'rxjs';
import {
  ICoreInsurerItem,
  IInsurerItem,
} from '../../../../models/insurer.model';
import {ApiInsurerService} from '../../../infrastructure/api/common-feature/api-insurer/api-insurer.service';
import {SortService} from '../../../infrastructure/utility/data/sort/sort.service';
import {selectInsurers} from '../../../../store/selector/insurer/insurer.selectors';
import {ErrorService} from '../../../infrastructure/page/error/error/error.service';
import {environment} from '../../../../../../environments/environment';
import {InsurerState} from '../../../../store/reducer/insurer/insurer.reducer';
import {InsurerActionsType} from '../../../../store/action/insurer/insurer.actions';
import {LobId} from '../../../../common/enums/lobId.enum';

@Injectable({
  providedIn: 'root',
})
export class InsurerService {

  private apiInsurerService = inject(ApiInsurerService);

  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  private sortService = inject(SortService);

  private errorService = inject(ErrorService);

  private store = inject<Store<InsurerState>>(Store);

  get insurerList(): IInsurerItem[] {
    return this.insurersData;
  }

  get coreInsurersList(): ICoreInsurerItem[] {
    return this.coreInsurersData;
  }

  public insurersData: IInsurerItem[] = [];

  public coreInsurersData: ICoreInsurerItem[] = [];

  public insurersWithActiveLobsAndAvailableProductData: ICoreInsurerItem[] = [];

  constructor() {
    this.initStoreData();
  }

  /**
   * Method: Initialization insurers data from Store
   */
  initStoreData(): void {
    this.store.pipe(select(selectInsurers), takeUntilDestroyed(this.destroyRef)).subscribe(data => {
      this.insurersData = data || [];
    });
  }

  /**
   * Method: Get all insurers
   * @param update
   */
  async getAllInsurers(update?: boolean): Promise<void> {
    if (!this.insurersData.length || !!update) {
      try {
        const result = await lastValueFrom(this.apiInsurerService.getAllInsurersObservable());
        this.insurersData = result.data.insurers ? this.sortByAlphabeticallyOrder(result.data.insurers) : [];
        this.store.dispatch({
          type: InsurerActionsType.load,
          payload: {
            insurers: this.insurersData,
          },
        });
        this.errorService.showResponseErrors(result);
      } catch (err) {
        this.errorService.showGraphQLErrors(err);
      }
    }
  }

  /**
   * Method: Get core insurers with active lob
   * @param lobId
   * @param contractTypes
   */
  async getCoreInsurersWithActiveLob(lobId: string, contractTypes: string[]): Promise<void> {
    try {
      const result = await lastValueFrom(
        this.apiInsurerService.getCoreInsurersWithActiveLobObservable(+lobId, contractTypes),
      );
      this.coreInsurersData = result?.data?.coreInsurersWithActiveLob
        ? this.sortCoreInsurerByAlphabeticallyOrder(result.data.coreInsurersWithActiveLob)
        : [];
      this.errorService.showResponseErrors(result);
    } catch (err) {
      this.errorService.showGraphQLErrors(err);
    }
  }

  /**
   * Method: Get list of active (not deleted) Insurers with active LoBs and at least one available Product
   * @param lobIds
   * @param contractTypes
   * @param interPension
   */
  async getInsurersWithActiveLobsAndAvailableProduct(lobIds: LobId[], contractTypes: string[], interPension: boolean)
    : Promise<void> {
    try {
      const result = await lastValueFrom(
        this.apiInsurerService.getInsurersWithActiveLobsAndAvailableProductObservable(
          lobIds,
          contractTypes,
          interPension,
        ),
      );
      const list = result?.data?.insurerInterview?.insurer?.withActiveLobsAndAvailableProduct || [];
      this.insurersWithActiveLobsAndAvailableProductData = this.sortCoreInsurerByAlphabeticallyOrder(list);
      this.errorService.showResponseErrors(result);
    } catch (err) {
      this.errorService.showGraphQLErrors(err);
    }
  }

  /**
   * Method: Gets an insurer object by its ID.
   * @param id - The ID of the insurer.
   * @returns The insurer object.
   */
  getInsurerById(id: string | number): IInsurerItem {
    if (!id) {
      return null;
    }
    return find(this.insurersData, ['id', id.toString()]) || null;
  }

  /**
   * Method: get core insurer object by id
   * @param id
   */
  getCoreInsurerById(id: string | number): ICoreInsurerItem {
    if (!id) {
      return null;
    }
    return find(this.coreInsurersData, ['id', id.toString()]) || null;
  }

  /**
   * Method: Get insurer with active LoBs and at least one available product by id
   * @param id
   */
  getInsurersWithActiveLobsAndAvailableProductById(id: string | number): ICoreInsurerItem {
    if (!id) {
      return null;
    }
    return find(this.insurersWithActiveLobsAndAvailableProductData, ['id', id.toString()]) || null;
  }

  /**
   * Method: Get core insurer item by id for insurer interview
   * @param id
   */
  getCoreInsurerItemByIdForInsurerInterview(id: string | number): ICoreInsurerItem {
    if (id) {
      if (this.coreInsurersData.length) {
        return this.getCoreInsurerById(id.toString());
      }
      if (this.insurersWithActiveLobsAndAvailableProductData.length) {
        return this.getInsurersWithActiveLobsAndAvailableProductById(id.toString());
      }
    }
    return null;
  }

  /**
   * Method: Sort insurer by alphabetically order for field shortName
   * @param insurerItems
   */
  sortByAlphabeticallyOrder(insurerItems: IInsurerItem[]): IInsurerItem[] {
    return this.sortService.sortByAlphabeticallyOrder(insurerItems, 'shortName');
  }

  /**
   * Method: Sort core insurer by alphabetically order for field shortName
   * @param insurerItems
   */
  sortCoreInsurerByAlphabeticallyOrder(insurerItems: ICoreInsurerItem[]): ICoreInsurerItem[] {
    return this.sortService.sortByAlphabeticallyOrderTranslatableString(insurerItems, 'shortName');
  }

  /**
   * Method: Get array for pre-selected insurers
   */
  preSelectData(selectedValue: string[]): string[] {
    if (selectedValue) {
      return Array.isArray(selectedValue) ? selectedValue : [selectedValue];
    }
    return [];
  }

  /**
   * Method: Get url logo
   * @param logo
   */
  getUrlLogo(logo: string): string {
    const server = environment.redirectLinkUrl;
    return logo ? server + logo : null;
  }

  /**
   * Method: Check long insurer name
   * @param name
   */
  isLongInsurerName(name: string): boolean {
    return name.length > 36;
  }

}
